<script setup>
    import { computed, inject, watch, onMounted } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { getPrettyPrice, getCashPrice, getCardPrice } from '../shared/utils'
    import { useModal, useModalSlot } from 'vue-final-modal'
    import { useProductStore } from '../store/products'
    import Modal from './Modal.vue'
    import ItemModal from './ItemModal.vue'
    import { useRouter, useRoute } from 'vue-router' // modal router
    import router from '../routes' // modal router

    // const router = useRouter() // modal router
    const route  = useRoute() // modal router
    let returnTo = null     // modal router: return to page

    let rootData = inject('rootData');
    const { t } = useI18n();
    const productStore = useProductStore();

    const props = defineProps({ item: Object })

    let tagIds = props.item.tags.length > 0  ? props.item.tags : [];
    let tags = [];
    tagIds.forEach(tagId => {
        if (productStore.tags.hasOwnProperty(tagId)) {
            let tag = Object.assign({}, productStore.tags[tagId])
            tags.push( tag );
        }
    });

    let image = computed(()=>{ return props.item.hasOwnProperty('img') && props.item.img.hasOwnProperty('medium_large') ? props.item.img.medium_large : null; });

    let has_price = computed(()=>{ return props.item.hasOwnProperty('price') && Number(props.item.price) > 0 ? true : false; });
    let price = props.item.hasOwnProperty('price') && Number(props.item.price) > 0 ? Number(props.item.price) : 0;
    let price_card = computed(()=> (has_price ? getCardPrice( Number(props.item.price) ) : 0));
    let price_cash = computed(()=> (has_price ? getCashPrice( Number(props.item.price) ) : 0));

    const { open: _open, close: _close } = useModal({
    component: Modal,
    attrs: {
      onClose() {
        handleClose()
      },
      escToClose: false,
    },
    slots: {
        default: useModalSlot({
            component: ItemModal,
            attrs: {
                item: productStore.items[props.item.id],
                onClose() {
                    handleClose()
                }
            }
        })
    }
  })

// Modal Open handler that wrapping open() and route modify
function handleOpen () {
  if (route.name !== 'product') {
    returnTo = route.fullPath          // save previous url path
  }

  if (route.params.id !== String(props.item.id)) {
    router.push({ name: 'product', params: { id: props.item.id } })
  }
  _open()
}

// Modal Close handler that wrapping close() and route modify
function handleClose () {
  _close()

  if (returnTo) {
    router.push(returnTo)          // normal open
    returnTo = null
  } else if (window.history.state && window.history.state.back !== null) {
    router.back()                  // direct open when history exists
  } else {
    router.push('/')               // first open - go home url
  }
}

// Modal auto open if current route already /product/:id
function isCurrentProductOpen () {
  return route.name === 'product' && route.params.id === String(props.item.id)
}
onMounted(() => {
  if (isCurrentProductOpen()) _open()
})

// watch for "manual" url changes, like address-bar typing
watch(
  () => route.fullPath,
  () => {
    if (isCurrentProductOpen()) {
      _open()
    } else {
      _close()
    }
  },
)
</script>

<template>
<div class="ez-card" :id="item.id" @click="handleOpen">
    <div class="ez-card__content">
        <div class="ez-card__head">
            <div class="ez-card__title">{{ item.name }}</div>
        </div>
        <div v-if="item.desc" class="ez-card__desc">{{ item.desc }}</div>
        <div v-if="has_price" class="ez-card__price" :data-single="!rootData.dualpricing ? '' : null">
            <template v-if="rootData.dualpricing">
                <div :data-label="t('payment.card')" v-text="getPrettyPrice(price_card)"></div>
                <div :data-label="t('payment.cash')" v-text="getPrettyPrice(price_cash)"></div>
            </template>
            <template v-else>
                <div data-label="" v-text="getPrettyPrice(price)"></div>
            </template>
        </div>
    </div>
    <div class="ez-card__iwrp">
        <img v-if="image" class="ez-card__img" :src="image" :alt="item.name">
        <div v-if="tags.length > 0" class="ez-card__tags"> 
            <div v-for="tag in tags" :style="[tag?.color ? {'--c-tag': tag.color} : '']">{{ tag.name }}</div>
        </div> 
    </div>
</div>
</template>
<style lang="scss">
.ez-card {
    position: relative;
    text-align: left;
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-basis: 100%;
    min-height: 120px;
    height: 154px;
    cursor: pointer;
    border: 1px solid #e7e9ed;
    border-radius: 0.375rem;
    padding: 0;
    background-color: #ffffff;
    transition: border-color 0.1s 0s linear;
    // box-shadow: 0 1px 4px rgba(37, 51, 56, .1);
    // margin-bottom: 1.5rem;

    // @media (min-width: 992px) {
    //     margin-bottom: 2rem;
    //     flex: 0 1 calc(50% - 24px);
    // } 

    &:hover {
        border: 1px solid #d7d7d7;
        box-sizing: border-box;
        color: inherit;
        text-decoration: none;
        transition: border-color .15s ease;
        overflow: hidden;
        transition: border-color 0.15s 0s linear;
    }

    &__content {
        flex-grow: 1;
        padding: 0.75rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        @media (min-width: 992px) {
            padding: 1rem;
        }
    }
    &__head {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.625rem;
    }

    &__title {
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        display: -webkit-box;
        margin: 0;
        overflow: hidden;
        font-size: 1rem;
        line-height: 1.2;
        font-weight: 600;
        letter-spacing: -0.01px;
    }
    &__desc {
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        display: -webkit-box;
        overflow: hidden;
        line-height: 1.6;
        font-size: 0.875rem;
        margin-bottom: 0.625rem;
    }
    &__iwrp {
        margin: -1px -1px -1px 0;
        overflow: hidden;
        width: 120px;
        flex: 0 0 120px;
        position: relative;

        @media (min-width: 992px) {
            flex: 0 0 154px;
        } 
    }
    &__img {
        min-height: 100%;
        min-width: 100%;
        border-radius: 0 4px 4px 0;
        padding: 0;
        -o-object-fit: cover;
        object-fit: cover;
        user-select: none;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }

    &__price {
        display: flex;
        justify-content: flex-start;
        margin-left: -.375rem;
        letter-spacing: -0.01px;
        
        > div {
            position: relative;
            display: flex;
            justify-content: center;
            flex: 0 0 auto;
            font-size: var(--font-size-sm);
            line-height: 1.25;
            margin-top: 1.25rem;
            padding-right: .375rem;
            padding-left: .375rem;
            color: #424242;
            font-weight: 500;
            
            // &:first-child {
            //     padding-right: .375rem;
            // }
            // &:last-child {
            //     padding-left: .375rem;
            // }
            &:first-child:not(:last-child)::after {
                content: "";
                width: 1px;
                height: 100%;
                background-color: var(--menu-separator-color);
                position: absolute;
                opacity: 0.75;
                right: 0;
                transform-origin: center;
                transform: translate3d(-50%,-1px,0);
                top: 0;
            }

            &::before {
                content: attr(data-label);
                text-align: center;
                position: absolute;
                top: 0;
                transform: translate3d(0, -125%, 0);
                transform: translate3d(0, -0.875rem, 0);
                width: 100%;
                line-height: 1;
                color: var(--menu-price-label-color);
                font-size: var(--font-size-2xs);
                font-weight: 500;
                line-height: 1.2;
                text-transform: uppercase;
            }

        }
        &[data-single] div::before,
        &[data-single] div::after {
            display: none;
        }
    }

    &__tags {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        padding: 0.75rem;

        div {
            background-color: var(--c-primary);
            background-color: var(--c-tag, var(--c-primary));
            color: white;
            border-radius: 2rem;
            line-height: 1;
            text-transform: uppercase;
            font-size: 0.625rem;
            font-weight: 500;
            padding: .25rem .75rem .2125rem;

            &:not(:last-child) {
                margin-bottom: 0.25rem;
            }
        }
    }
}

</style>